import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineInfo, MdOutlineWarningAmber } from "react-icons/md";
import { Section, SettingRow, Toggle } from "./SettingsControls";
import type { SendMethod, LetterTexts, PdfSettings, DunningFines } from "./useSettings";
import { LETTER_DEFAULTS } from "src/config/letterDefaults";
import { apiRequest } from "src/services/apiClient";
import { useAuth } from "src/context/AuthProvider";

const ENVELOPE_KEYS = [
    { value: "DIN_LANG_FENSTER_LINKS", key: "postEnvelopeWindowLeft" },
    { value: "C4_OHNE_FENSTER",        key: "postEnvelopeC4" },
] as const;

type Props = {
    view?: "post" | "templates";
    sendMethods: SendMethod[];
    postColor: boolean;
    postDuplex: boolean;
    postDispatch: string;
    postEnvelope: string;
    postShipping: string;
    postSendOnUpload: boolean;
    letterTexts: LetterTexts;
    pdfSettings: PdfSettings;
    dunningFinesEnabled?: boolean;
    dunningFines?: DunningFines;
    /** Cumulative days-from-sent per dunning level ([0, M1day, M2day, M3day, ...]) — used so
     *  the template preview's sample dates reflect the praxis's actual configured intervals
     *  instead of a generic fixed offset. */
    dunningDays?: number[];
    saved: boolean;
    dirty?: boolean;
    onTogglePost: () => void;
    onColorChange: (v: boolean) => void;
    onDuplexChange: (v: boolean) => void;
    onDispatchChange: (v: string) => void;
    onEnvelopeChange: (v: string) => void;
    onShippingChange: (v: string) => void;
    onPostSendOnUploadChange: (v: boolean) => void;
    onLetterTextsChange: (t: LetterTexts) => void;
    onSave: () => void;
};

type LetterKey = keyof LetterTexts;

const LETTER_VARS = [
    "{recipient.name}",
    "{invoice.id}", "{invoice.date}", "{invoice.dueDate}",
    "{invoice.totalWithFine}", "{invoice.table}",
    "{doctor.name}", "{doctor.title}",
    "{praxis.name}", "{praxis.address}", "{praxis.plz}", "{praxis.city}",
    "{praxis.iban}", "{praxis.phone}", "{praxis.email}",
    "{fine}", "{M1-date}", "{M2-date}",
];

function VarChips({ vars, onInsert }: { vars: string[]; onInsert: (v: string) => void }) {
    return (
        <div className="pdf-var-chips">
            {vars.map((v) => (
                <button key={v} type="button" className="pdf-var-chip" onClick={() => onInsert(v)}>
                    {v}
                </button>
            ))}
        </div>
    );
}

const HEADER_SLOTS = ["left", "center", "right"] as const;
type HeaderSlot = typeof HEADER_SLOTS[number];
const DUNNING_FINE_LEVELS = ["friendly", "demanding", "final"] as const;
const SAMPLE_INVOICE_AMOUNT = 90;

const fmtEUR = (n: number) =>
    n.toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function PostSettings({
    view = "post",
    sendMethods,
    postColor,
    postDuplex,
    postDispatch,
    postEnvelope,
    postShipping,
    postSendOnUpload,
    letterTexts,
    pdfSettings,
    dunningFinesEnabled,
    dunningFines,
    dunningDays,
    saved,
    dirty,
    onTogglePost,
    onColorChange,
    onDuplexChange,
    onDispatchChange,
    onEnvelopeChange,
    onShippingChange,
    onPostSendOnUploadChange,
    onLetterTextsChange,
    onSave,
}: Props) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [templateTab, setTemplateTab] = useState<LetterKey>("friendly");
    const [editingBody, setEditingBody] = useState(false);
    const [pages, setPages] = useState(1);
    const [estimatedCost, setEstimatedCost] = useState<number | null>(null);
    const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
    const postEnabled = sendMethods.includes("post");
    const isTrialing = user?.praxis?.subscriptionStatus === "trialing" &&
        !!user?.praxis?.trialEndsAt &&
        new Date(user.praxis.trialEndsAt) > new Date();

    useEffect(() => {
        const params = new URLSearchParams({
            pages:      String(pages),
            color:      String(postColor),
            duplex:     String(postDuplex),
            envelope:   postEnvelope,
            shipping:   postShipping || "national",
            registered: postDispatch || "",
        });
        apiRequest<{ price: number }>(`/settings/lx-price?${params}`)
            .then((d) => setEstimatedCost(d.price))
            .catch(() => setEstimatedCost(null));
    }, [pages, postColor, postDuplex, postEnvelope, postShipping, postDispatch]);

    const registeredMailLocked = postDispatch === "r1" || postDispatch === "r2";

    useEffect(() => {
        if (registeredMailLocked && postShipping === "international") {
            onShippingChange("national");
        }
    }, [registeredMailLocked]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset edit mode when switching template tabs
    useEffect(() => {
        setEditingBody(false);
    }, [templateTab]);

    // ── Letter template preview ──────────────────────────────────────────────

    const currentFine = dunningFinesEnabled && (DUNNING_FINE_LEVELS as readonly string[]).includes(templateTab)
        ? (dunningFines?.[templateTab as typeof DUNNING_FINE_LEVELS[number]] ?? 0)
        : 0;

    // Sample dates for an invoice sent today ({invoice.date}), counted forward from the praxis's
    // configured dunning days (cumulative days after sending). Mirrors the backend exactly:
    // dueDate = sentDate + cumDays[1] (same for every level — it's the invoice's due date), and
    // dunningCron sends level L on sentDate + cumDays[L] + 1 (the +1 buffer day in dunningCron).
    const cumDays = dunningDays?.length ? dunningDays : [0, 7, 14, 21, 28];
    const addDays = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toLocaleDateString("de-DE"); };
    const daysTo = (level: number) => cumDays[level] ?? cumDays[1] ?? 7;
    const dueDateStr = addDays(daysTo(1));
    const m1DateStr  = addDays(daysTo(1) + 1);
    const m2DateStr  = addDays(daysTo(2) + 1);

    const SAMPLE_VARS: Record<string, string> = {
        "{recipient.name}":         "Erika Mustermann",
        "{invoice.id}":             "RE-2024-0001",
        "{invoice.date}":           new Date().toLocaleDateString("de-DE"),
        "{invoice.dueDate}":        dueDateStr,
        "{M1-date}":                m1DateStr,
        "{M2-date}":                m2DateStr,
        "{invoice.totalWithFine}":  fmtEUR(SAMPLE_INVOICE_AMOUNT + currentFine),
        "{doctor.name}":            [user?.doctor?.title, user?.doctor?.firstName, user?.doctor?.lastName].filter(Boolean).join(" ") || "Dr. Max Mustermann",
        "{doctor.title}":           user?.doctor?.title ?? "Dr.",
        "{doctor.salutation}":      user?.doctor?.salutation ?? "Herr",
        "{praxis.name}":            user?.praxis?.name ?? "Muster Praxis",
        "{praxis.address}":         user?.praxis?.street ?? "Musterstraße 1",
        "{praxis.plz}":             user?.praxis?.plz ?? "12345",
        "{praxis.city}":            user?.praxis?.city ?? "Berlin",
        // Doctor's own payout IBAN takes priority — praxis.iban can be overwritten by the
        // platform billing SEPA mandate and must never be shown as the patient payout account
        // unless the doctor hasn't set their own (matches backend resolveEmailVars.ts).
        "{praxis.iban}":            user?.doctor?.iban || (user?.praxis as any)?.iban || "DE12 3456 7890 1234 5678 90",
        "{praxis.phone}":           ((user?.praxis as any)?.tel ?? [])[0] ?? "+49 30 123456",
        "{praxis.email}":           user?.praxis?.email ?? "praxis@example.com",
        "{fine}":                   fmtEUR(currentFine),
    };

    // Plain-text "invoice details" table for {invoice.table} — mirrors backend/src/utils/resolveEmailVars.ts
    const buildInvoiceTableStr = () => {
        const rows: [string, string][] = [
            ["Rechnungsnummer",  SAMPLE_VARS["{invoice.id}"]],
            ["Fälligkeitsdatum", SAMPLE_VARS["{invoice.dueDate}"]],
            ["Betrag", fmtEUR(SAMPLE_INVOICE_AMOUNT)],
            ...(currentFine > 0 ? ([["Mahngebühr", SAMPLE_VARS["{fine}"]]] as [string, string][]) : []),
            ["IBAN", SAMPLE_VARS["{praxis.iban}"]],
        ];
        const visibleRows = rows.filter(([, value]) => value);
        const labelWidth   = Math.max(0, ...visibleRows.map(([label]) => label.length)) + 1;
        return visibleRows
            .map(([label, value]) => `${(label + ":").padEnd(labelWidth + 1)}${value}`)
            .join("\n");
    };

    const renderLetterTemplate = (raw: string) => {
        // Resolve {if:fine}...{/if} — only show when this level actually has a fine configured
        let text = raw.replace(/\{if:fine\}([\s\S]*?)\{\/if\}/g, currentFine > 0 ? "$1" : "");
        // Resolve {invoice.table} into a plain-text table before the generic token replace
        text = text.replace(/\{invoice\.table\}/g, buildInvoiceTableStr());
        // Replace tokens
        return Object.entries(SAMPLE_VARS).reduce((s, [k, v]) => s.replaceAll(k, v), text);
    };

    const LETTER_TEMPLATE_ROWS: { key: LetterKey; label: string }[] = [
        { key: "friendly", label: `M1 – ${t("settings.emailTemplateFriendly")}` },
        { key: "demanding",label: `M2 – ${t("settings.emailTemplateDemanding")}` },
        { key: "final",    label: `M3 – ${t("settings.emailTemplateFinal")}` },
    ];

    const startEditingBody = () => {
        if (!letterTexts[templateTab]) {
            onLetterTextsChange({ ...letterTexts, [templateTab]: (LETTER_DEFAULTS as any)[templateTab]?.text ?? "" });
        }
        setEditingBody(true);
        setTimeout(() => bodyTextareaRef.current?.focus(), 40);
    };

    // ── Invoice-PDF header, mirrored from generateInvoicePDF/pageLayout.ts renderHeader() ──
    const hasCustomHeader = !!(pdfSettings.headerLeft || pdfSettings.headerCenter || pdfSettings.headerRight || pdfSettings.headerLogoSlot !== "none");

    const headerSlotText = (slot: HeaderSlot): string => {
        const raw = slot === "left" ? pdfSettings.headerLeft : slot === "center" ? pdfSettings.headerCenter : pdfSettings.headerRight;
        if (!raw) return "";
        return Object.entries(SAMPLE_VARS).reduce((s, [k, v]) => s.replaceAll(k, v), raw);
    };
    const headerSlotFont = (slot: HeaderSlot) => {
        const cap = slot.charAt(0).toUpperCase() + slot.slice(1);
        return {
            color:  (pdfSettings as any)[`header${cap}FontColor`] || "#555555",
            size:   (pdfSettings as any)[`header${cap}FontSize`] || 7.5,
            weight: (pdfSettings as any)[`header${cap}FontWeight`] || "normal",
        };
    };

    const RadioGroup = ({
        name, options, value, onChange,
    }: {
        name: string;
        options: { value: string | boolean; label: string; disabled?: boolean }[];
        value: string | boolean;
        onChange: (v: any) => void;
    }) => (
        <div className="lx-options__choices">
            {options.map((opt) => (
                <label
                    key={String(opt.value)}
                    className={`lx-options__choice${value === opt.value ? " lx-options__choice--selected" : ""}${opt.disabled ? " lx-options__choice--disabled" : ""}`}
                >
                    <input
                        type="radio"
                        name={name}
                        checked={value === opt.value}
                        disabled={opt.disabled}
                        onChange={() => !opt.disabled && onChange(opt.value)}
                    />
                    {opt.label}
                </label>
            ))}
        </div>
    );

    return (
        <Section title={view === "post" ? t("settings.post") : t("settings.tabLetterTemplates")}>
        {view === "post" && (
            <>
                {/* Trial notice */}
                {isTrialing && (
                    <div className="settings-info-banner settings-info-banner--warn">
                        <MdOutlineWarningAmber size={16} />
                        {t("settings.postTrialDisabled")}
                    </div>
                )}

                {/* Billing notice */}
                {!isTrialing && (
                    <div className="settings-info-banner settings-info-banner--warn">
                        <MdOutlineWarningAmber size={16} />
                        {t("settings.postBillingNotice")}
                    </div>
                )}

                <SettingRow title={t("settings.postEnable")} description={t("settings.postEnableDesc")}>
                    <Toggle value={postEnabled && !isTrialing} onChange={isTrialing ? () => {} : onTogglePost} disabled={isTrialing} />
                </SettingRow>

                {postEnabled && (
                    <>
                        <div className="settings-info-banner settings-info-banner--neutral post-cost-banner post-cost-banner--sticky">
                            <MdOutlineInfo size={16} />
                            <span>
                                {t("settings.postEstimatedCost")}
                                {" "}
                                {estimatedCost != null
                                    ? <>€{estimatedCost.toFixed(2)}{" "}({pages} pages)</>
                                    : "…"
                                }
                            </span>
                        </div>

                        <SettingRow title={t("settings.postPages")} description={t("settings.postPagesDesc")}>
                            <input
                                type="number"
                                min={1}
                                className="settings-input settings-input--small"
                                value={pages}
                                onChange={(e) => setPages(Math.max(1, parseInt(e.target.value) || 1))}
                            />
                        </SettingRow>

                        <SettingRow title={t("settings.postColor")} description={t("settings.postColorDesc")}>
                            <RadioGroup
                                name="postColor"
                                value={postColor}
                                onChange={onColorChange}
                                options={[
                                    { value: false, label: t("settings.postColorBW") },
                                    { value: true,  label: t("settings.postColorOption") },
                                ]}
                            />
                        </SettingRow>

                        <SettingRow title={t("settings.postPrint")} description={t("settings.postPrintDesc")}>
                            <RadioGroup
                                name="postDuplex"
                                value={postDuplex}
                                onChange={onDuplexChange}
                                options={[
                                    { value: false, label: t("settings.postPrintSimplex") },
                                    { value: true,  label: t("settings.postPrintDuplex") },
                                ]}
                            />
                        </SettingRow>

                        <SettingRow title={t("settings.postEnvelope")} description={t("settings.postEnvelopeDesc")}>
                            <RadioGroup
                                name="postEnvelope"
                                value={postEnvelope}
                                onChange={onEnvelopeChange}
                                options={ENVELOPE_KEYS.map((o) => ({ value: o.value, label: t(`settings.${o.key}`) }))}
                            />
                        </SettingRow>

                        <SettingRow title={t("settings.postRegisteredMail")} description={t("settings.postRegisteredMailDesc")}>
                            <RadioGroup
                                name="postDispatch"
                                value={postDispatch}
                                onChange={onDispatchChange}
                                options={[
                                    { value: "",    label: t("settings.postRegisteredMailNone") },
                                    { value: "r1",  label: t("settings.postRegisteredMailR1") },
                                    { value: "r2",  label: t("settings.postRegisteredMailR2") },
                                ]}
                            />
                        </SettingRow>

                        <div className="settings-info-banner settings-info-banner--neutral">
                            <MdOutlineInfo size={16} />
                            {t("settings.postRegisteredMailNationalOnly")}
                        </div>

                        <SettingRow title={t("settings.postShipping")} description={t("settings.postShippingDesc")}>
                            <RadioGroup
                                name="postShipping"
                                value={postShipping}
                                onChange={onShippingChange}
                                options={[
                                    { value: "national",      label: t("settings.postShippingNational") },
                                    { value: "international", label: t("settings.postShippingInternational"), disabled: registeredMailLocked },
                                ]}
                            />
                        </SettingRow>

                        <SettingRow title={t("settings.sendOnUpload")} description={t("settings.postSendOnUploadDesc")}>
                            <Toggle value={postSendOnUpload} onChange={onPostSendOnUploadChange} />
                        </SettingRow>
                    </>
                )}
            </>
        )}

        {view === "templates" && (() => {
            const rawText  = letterTexts[templateTab] || (LETTER_DEFAULTS as any)[templateTab]?.text || "";
            const bodyText = renderLetterTemplate(rawText);
            const cityStr    = SAMPLE_VARS["{praxis.city}"];
            const dateStr    = new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
            const senderLine = [
                SAMPLE_VARS["{doctor.name}"],
                SAMPLE_VARS["{praxis.address}"],
                `${SAMPLE_VARS["{praxis.plz}"]} ${SAMPLE_VARS["{praxis.city}"]}`,
            ].join(" · ");

            return (
                <>
                    <div className="settings-info-banner settings-info-banner--neutral">
                        <MdOutlineInfo size={16} />
                        {t("settings.letterTemplateLegalInfo")}
                    </div>
                    <div className="settings-tabs settings-tabs--secondary">
                        {LETTER_TEMPLATE_ROWS.map(({ key, label }) => (
                            <button
                                key={key}
                                className={`settings-tab settings-tab--sm${templateTab === key ? " settings-tab--active" : ""}`}
                                onClick={() => setTemplateTab(key)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    <div className="letter-preview">
                        {/* Header — mirrors the configured invoice-PDF header */}
                        {hasCustomHeader ? (
                            <div
                                className="letter-preview__header-pdf"
                                style={{
                                    "--lh-border-color": pdfSettings.headerBorderColor || "transparent",
                                    "--lh-border-width": `${pdfSettings.headerBorderWidth ?? 0}px`,
                                    "--lh-pad-h": `${pdfSettings.headerPaddingH ?? 0}px`,
                                    "--lh-pad-v": `${pdfSettings.headerPaddingV ?? 0}px`,
                                } as React.CSSProperties}
                            >
                                {HEADER_SLOTS.map((slot) => {
                                    const isLogoSlot = pdfSettings.headerLogo && pdfSettings.headerLogoSlot === slot;
                                    const font = headerSlotFont(slot);
                                    const text = headerSlotText(slot);
                                    return (
                                        <div key={slot} className={`letter-preview__header-col letter-preview__header-col--${slot}`}>
                                            {isLogoSlot && (
                                                <img
                                                    src={pdfSettings.headerLogo}
                                                    alt="logo"
                                                    className="letter-preview__header-logo"
                                                    style={{ "--lh-logo-size": `${pdfSettings.headerLogoSize ?? 34}px` } as React.CSSProperties}
                                                />
                                            )}
                                            {text && (
                                                <span
                                                    className="letter-preview__header-text"
                                                    style={{
                                                        "--lh-color":  font.color,
                                                        "--lh-size":   `${font.size}px`,
                                                        "--lh-weight": font.weight,
                                                    } as React.CSSProperties}
                                                >
                                                    {text}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="letter-preview__header">
                                <span className="letter-preview__praxis-name">{SAMPLE_VARS["{praxis.name}"]}</span>
                                <span className="letter-preview__praxis-sub">
                                    {SAMPLE_VARS["{praxis.address}"]} · {SAMPLE_VARS["{praxis.plz}"]} {SAMPLE_VARS["{praxis.city}"]}
                                    {SAMPLE_VARS["{praxis.phone}"] && ` · ${SAMPLE_VARS["{praxis.phone}"]}`}
                                </span>
                            </div>
                        )}
                        <div className="letter-preview__header-rule" />

                        {/* Address zone + date */}
                        <div className="letter-preview__address-zone">
                            <div className="letter-preview__address-window">
                                <div className="letter-preview__sender-return">{senderLine}</div>
                                <div className="letter-preview__recipient">
                                    <div>Erika Mustermann</div>
                                    <div>Hauptstraße 12</div>
                                    <div>10115 Berlin</div>
                                </div>
                            </div>
                            <div className="letter-preview__date-block">
                                <div>{cityStr}, {dateStr}</div>
                            </div>
                        </div>

                        {/* Reference */}
                        <div className="letter-preview__reference">
                            {((LETTER_DEFAULTS as any)[templateTab]?.subject ?? "Rechnung {invoice.id}").replace("{invoice.id}", SAMPLE_VARS["{invoice.id}"])}
                        </div>

                        {/* Body + QR (always shown — scan & pay is always included on dunning letters) */}
                        <div className="letter-preview__content letter-preview__content--with-qr">
                            {editingBody ? (
                                <div className="email-tpl__edit-zone">
                                    <textarea
                                        ref={bodyTextareaRef}
                                        className="email-tpl__textarea"
                                        value={letterTexts[templateTab]}
                                        onChange={(e) => onLetterTextsChange({ ...letterTexts, [templateTab]: e.target.value })}
                                        placeholder={(LETTER_DEFAULTS as any)[templateTab]?.text ?? ""}
                                        rows={10}
                                    />
                                    <VarChips
                                        vars={LETTER_VARS}
                                        onInsert={(v) => {
                                            const el = bodyTextareaRef.current;
                                            if (el) {
                                                const start = el.selectionStart;
                                                const end   = el.selectionEnd;
                                                const cur   = letterTexts[templateTab];
                                                const next  = cur.slice(0, start) + v + cur.slice(end);
                                                onLetterTextsChange({ ...letterTexts, [templateTab]: next });
                                                setTimeout(() => {
                                                    el.focus();
                                                    el.setSelectionRange(start + v.length, start + v.length);
                                                }, 0);
                                            } else {
                                                onLetterTextsChange({ ...letterTexts, [templateTab]: letterTexts[templateTab] + v });
                                            }
                                        }}
                                    />
                                    <div className="email-template-hint">
                                        <strong>{"{if:fine}"}…{"{/if}"}</strong> — dieser Block erscheint nur, wenn eine Mahngebühr konfiguriert ist.
                                    </div>
                                    {letterTexts[templateTab] && (
                                        <button
                                            type="button"
                                            className="email-tpl__reset-btn"
                                            onClick={() => onLetterTextsChange({ ...letterTexts, [templateTab]: "" })}
                                        >
                                            Standard wiederherstellen
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="email-tpl__done-btn"
                                        onClick={() => setEditingBody(false)}
                                    >
                                        Fertig
                                    </button>
                                </div>
                            ) : (
                                <div
                                    className="letter-preview__body email-tpl__view-zone"
                                    onClick={startEditingBody}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => e.key === "Enter" && startEditingBody()}
                                >
                                    {bodyText.split("\n").map((line, i) => (
                                        <span key={i}>{line || " "}<br /></span>
                                    ))}
                                    <span className="email-tpl__edit-hint">Klicken zum Bearbeiten</span>
                                </div>
                            )}
                            <div className="letter-preview__qr">
                                <div className="letter-preview__qr-box">QR</div>
                                <div className="letter-preview__qr-label">SCAN &amp; PAY</div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="letter-preview__footer-rule" />
                        <div className="letter-preview__footer">
                            <span>{SAMPLE_VARS["{praxis.name}"]}</span>
                            {SAMPLE_VARS["{praxis.iban}"] && (
                                <span>IBAN: {SAMPLE_VARS["{praxis.iban}"]}</span>
                            )}
                            {SAMPLE_VARS["{praxis.email}"] && (
                                <span>{SAMPLE_VARS["{praxis.email}"]}</span>
                            )}
                        </div>
                    </div>
                </>
            );
        })()}
        </Section>
    );
}
